import React, { useMemo } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CartSummary } from "@/modules/checkout/components/CartSummary/CartSummary";
import { useCustomerInfoStep } from "../../hooks/useCustomerInfoStep";
import { buildProductCart } from "../../utils/buildProductCart";

export const CustomerInfoStep: React.FC = () => {
  const {
    customerInfo,
    orderNotes,
    cartItems,
    errors,
    setCustomerInfo,
    setOrderNotes,
    handleContinue,
  } = useCustomerInfoStep();

  const cart = useMemo(() => buildProductCart({ cartItems }), [cartItems]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        {/* Customer Information */}
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6">
          <h2 className="text-base font-medium text-gray-900 dark:text-white mb-4">
            Customer Information
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName">
                First Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="firstName"
                onChange={(e) =>
                  setCustomerInfo({
                    ...customerInfo,
                    firstName: e.target.value,
                  })
                }
                value={customerInfo.firstName}
              />
              {errors.firstName && (
                <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>
              )}
            </div>
            <div>
              <Label htmlFor="lastName">
                Last Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="lastName"
                onChange={(e) =>
                  setCustomerInfo({
                    ...customerInfo,
                    lastName: e.target.value,
                  })
                }
                value={customerInfo.lastName}
              />
              {errors.lastName && (
                <p className="text-xs text-red-500 mt-1">{errors.lastName}</p>
              )}
            </div>
            <div>
              <Label htmlFor="email">
                Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                onChange={(e) =>
                  setCustomerInfo({
                    ...customerInfo,
                    email: e.target.value,
                  })
                }
                type="email"
                value={customerInfo.email}
              />
              {errors.email && (
                <p className="text-xs text-red-500 mt-1">{errors.email}</p>
              )}
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                onChange={(e) =>
                  setCustomerInfo({
                    ...customerInfo,
                    phone: e.target.value,
                  })
                }
                type="tel"
                value={customerInfo.phone}
              />
            </div>
          </div>
        </div>

        {/* Order Notes */}
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6">
          <h2 className="text-base font-medium text-gray-900 dark:text-white mb-2">
            Order Notes
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Any special instructions for your order
          </p>
          <textarea
            className="w-full rounded-md border border-input bg-white dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100 px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring min-h-[100px]"
            onChange={(e) => setOrderNotes(e.target.value)}
            placeholder="Add any notes or special instructions..."
            value={orderNotes}
          />
        </div>
      </div>

      {/* Right column - Order Summary */}
      <div>
        <CartSummary cart={cart} showSecurePaymentFooter={false}>
          <Button className="w-full" onClick={handleContinue} type="button">
            Continue to Payment <ArrowRight className="h-4 w-4" />
          </Button>
        </CartSummary>
      </div>
    </div>
  );
};
